import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Check } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@meno/ui/components/ui/dialog'
import { Input } from '@meno/ui/components/ui/input'

// A single-select modal picker — the same "open a modal to choose" flow the
// media/links tabs use (PlaylistContentPicker), but for the single-value
// companion tabs (audio, stop screen, transition). Options may carry a `group`
// label; a "None" row clears the selection. Picking a row selects + closes.
export type CompanionOption = { value: string; label: string; sublabel?: string; group?: string }

export default function CompanionPickerDialog({
  open, title, options, selected, onSelect, onClose, searchPlaceholder,
}: {
  open: boolean
  title: string
  options: CompanionOption[]
  selected: string | null
  onSelect: (value: string | null) => void
  onClose: () => void
  searchPlaceholder?: string
}) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return needle ? options.filter(o => o.label.toLowerCase().includes(needle)) : options
  }, [options, q])

  // Preserve group order as first-seen in the (filtered) options list.
  const groups = useMemo(() => {
    const order: string[] = []
    const byGroup = new Map<string, CompanionOption[]>()
    for (const o of filtered) {
      const g = o.group ?? ''
      if (!byGroup.has(g)) { byGroup.set(g, []); order.push(g) }
      byGroup.get(g)!.push(o)
    }
    return order.map(g => ({ group: g, items: byGroup.get(g)! }))
  }, [filtered])

  const pick = (value: string | null) => { onSelect(value); onClose() }

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent className="max-w-md p-0 overflow-hidden flex flex-col max-h-[80vh]">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="px-5 pb-3">
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder={searchPlaceholder ?? t('common.search', 'Search')}
              className="ps-9 h-9"
              autoFocus
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto border-t border-border">
          {/* None clears the companion. */}
          <button
            type="button"
            onClick={() => pick(null)}
            className={`w-full flex items-center justify-between gap-2 px-5 py-2.5 text-sm text-start transition-colors ${selected == null ? 'bg-muted' : 'hover:bg-muted/50'}`}
          >
            <span className="text-muted-foreground">{t('companions.none', 'None')}</span>
            {selected == null && <Check className="w-4 h-4 text-primary shrink-0" />}
          </button>

          {groups.map(({ group, items }) => (
            <div key={group || '_'}>
              {group && (
                <p className="px-5 pt-3 pb-1 text-[11px] uppercase tracking-wider text-muted-foreground">{group}</p>
              )}
              {items.map(o => {
                const active = selected === o.value
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => pick(o.value)}
                    className={`w-full flex items-center justify-between gap-2 px-5 py-2.5 text-start transition-colors ${active ? 'bg-muted' : 'hover:bg-muted/50'}`}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium truncate">{o.label}</span>
                      {o.sublabel && <span className="block text-[11px] text-muted-foreground truncate">{o.sublabel}</span>}
                    </span>
                    {active && <Check className="w-4 h-4 text-primary shrink-0" />}
                  </button>
                )
              })}
            </div>
          ))}

          {filtered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-10">{t('common.noData', 'No data found')}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
